/**
 * Snippets Store - Centralized State Management
 *
 * This module provides a centralized store for managing snippets data and filter state
 * using react-sweet-state. This replaces the previous pattern of individual hooks
 * (useCreateSnippet, useFetchSnippets) with a single source of truth.
 *
 * Usage:
 * - Wrap your app with <SnippetsProvider> to trigger initial fetch
 * - Use useSnippetsState() for read-only state access
 * - Use useSnippetsActions() when you need to dispatch actions
 * - Use useSnippets() when you need both state and actions
 */
import {
	createStore,
	createHook,
	createActionsHook,
	createStateHook,
	defaultRegistry,
} from 'react-sweet-state';

import type { SnippetsState } from './snippetsStore.types.ts';
import type { Snippet } from '../snippetTypes.ts';

import { getInitialState } from './snippetsStore.types.ts';
import { actions } from './actions/index.ts';

/**
 * Module-level registry for one-time callbacks that fire when the current in-flight fetch completes.
 * These callbacks are cleared after each fetch completes and are NOT triggered by future fetches.
 */
let pendingLoadCallbacks: Array<(snippets: Snippet[]) => void> = [];

export const SnippetsStore = createStore({
	initialState: getInitialState(),
	actions,
	name: 'SnippetsStore',
});

/** Hook to access both state and actions - use when you need full store access */
export const useSnippets = createHook(SnippetsStore);

/** Hook to access only actions - use when component only dispatches actions */
export const useSnippetsActions = createActionsHook(SnippetsStore);

/** Hook to access only state - use for read-only state consumption */
export const useSnippetsState = createStateHook(SnippetsStore);

/**
 * Gets the current snippets state without subscribing to updates.
 * Use this when you need a one-time snapshot of the state outside of React's render cycle,
 * or when you want to avoid re-renders when the state changes.
 */
export const getSnippetsStateSnapshot = (): SnippetsState => {
	const storeInstance = defaultRegistry.getStore(SnippetsStore);
	return storeInstance.storeState.getState();
};

/**
 * Registers a one-time callback that fires when the current in-flight fetch completes.
 *
 * The caller is responsible for checking the state before calling this function.
 * Only call this when you know a fetch is in progress (isLoading=true && isLoaded=false).
 *
 * This is useful when a consumer takes a snapshot and discovers data is still loading -
 * they can register a callback to be notified when the fetch completes without
 * subscribing to store updates.
 *
 * @param callback - Function to call with the loaded snippets when fetch completes
 */
export const onCurrentFetchComplete = (callback: (snippets: Snippet[]) => void): void => {
	pendingLoadCallbacks.push(callback);
};

/**
 * Invokes all pending load callbacks and clears the registry.
 * Called internally by fetchActions when a fetch completes successfully.
 * @internal
 */
export const invokePendingLoadCallbacks = (snippets: Snippet[]): void => {
	const callbacks = pendingLoadCallbacks;
	pendingLoadCallbacks = []; // Clear before invoking to prevent re-registration issues
	callbacks.forEach((cb) => cb(snippets));
};
